import { Box, Typography } from '@mui/material';
import { FieldValue, QueryRule } from '../../../../../types/QueryBuilder';
import { useLanguage } from '../../../../../context/LanguageContext';
import TriStateToggle from './TriStateToggle';
import { parseSubscriptionSelections, toStringList } from './valueUtils';

interface SubscriptionTogglesProps {
    value: QueryRule['value'];
    options: FieldValue[];
    onChange: (value: string[]) => void;
}

const NO_RESPONSE_VALUE = 'no_preference';

function SubscriptionToggles({ value, options, onChange }: SubscriptionTogglesProps) {
    const { t } = useLanguage();
    const entries = toStringList(value);
    const selectionByName = parseSubscriptionSelections(entries);
    const labels = {
        accept: t('pages.clients.queryBuilder.simple.subscription.subscribed'),
        refuse: t('pages.clients.queryBuilder.simple.subscription.unsubscribed'),
        noPreference: t('pages.clients.queryBuilder.simple.subscription.noResponse'),
    };

    const handleSelect = (optionName: string, selection: string | null) => {
        const next = entries.filter(entry => !entry.startsWith(`${optionName}:`));
        if (selection !== null) next.push(`${optionName}:${selection}`);
        onChange(next);
    };

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 1.5 }}>
            {options.map(option => (
                <Box key={option.name} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ color: 'grey.900', fontWeight: 700 }}>
                        {option.label}
                    </Typography>
                    <TriStateToggle
                        value={selectionByName.get(option.name) ?? null}
                        noPreferenceValue={NO_RESPONSE_VALUE}
                        labels={labels}
                        onChange={selection => handleSelect(option.name, selection)}
                    />
                </Box>
            ))}
        </Box>
    );
}

export default SubscriptionToggles;
